import { getCatalogCountries } from '../../../api/countries';
import { useBooking, useApp } from '../../../hooks';
import {
  useHydrateTripCore,
  useTripStore,
  useTripFilters,
  useSetTripFilters,
} from '@koob/trip-designer';
import { pushEvent } from '@koob/google-tag-manager';
import { GTM_EVENTS, TRIP_ORIGINS } from '@koob/google-tag-manager/src/events';
import { useKoobStore, useActiveFolder } from 'store';
import { createDefaultClientFolder } from 'utils/customerFile';
import { generateTravelerComposition } from '@koob/utils';
import { isInvalidDepartureDate } from '@koob/ui/src/features/tripWizard';

/**
 * Loads a trip into the Trip Designer context + composer store — what the
 * wizard reads. Used by the /design-trip/:fromId route and by the wizard's
 * template picker, which opens the trip in place rather than navigating.
 */
export function useHydrateTrip() {
  const setTripFilters = useSetTripFilters();
  const tripFilters = useTripFilters();
  const setInitialExtras = useTripStore(state => state.setInitialExtras);
  const { setCustomerFile } = useBooking();
  const hydrateTripCore = useHydrateTripCore();
  const activeFolder = useActiveFolder();
  const openFolder = useKoobStore(state => state.openFolder);
  const { currentOrganizationIdInt, user } = useApp();

  // Ownership can only be judged once the current organization is known; until
  // then nothing is declared foreign, so a trip is never moved off its folder
  // just because `/api/v1/user` has not answered yet.
  const isOurs = organizationId =>
    Boolean(currentOrganizationIdInt) &&
    Boolean(organizationId) &&
    String(organizationId) === String(currentOrganizationIdInt);

  const isForeign = organizationId =>
    Boolean(currentOrganizationIdInt) &&
    Boolean(organizationId) &&
    !isOurs(organizationId);

  /** The folder the TO works in, or a fresh one when it has none. */
  const ownClientFolder = async trip =>
    activeFolder ??
    tripFilters?.clientFolder ??
    (await createDefaultClientFolder(
      currentOrganizationIdInt,
      user,
      trip?.locale,
    ));

  /**
   * The client folder this trip opens on, which must always be one of ours.
   * A trip distributed by a DMC — a template, a proposal — belongs to that DMC
   * and carries that DMC's folder: `GET /folder/:id` only serves the caller's
   * own folders, so the TO can neither read it nor save a draft into it. Such a
   * trip opens on the folder the TO is working in, or on one created on the
   * spot, so it always opens on a folder that belongs to the TO.
   */
  const resolveClientFolder = async trip => {
    const tripFolder = trip.clientFolder;

    // A DMC template has no folder of ours to reuse, whether or not it carries
    // one of the DMC's — the TO gets its own either way.
    if (
      isForeign(trip.organisationId) ||
      isForeign(tripFolder?.organization?.id)
    ) {
      return await ownClientFolder(trip);
    }

    // Ours, or an owner the API did not send: leave the trip on its own folder,
    // as before, rather than moving every draft to the active folder.
    return tripFolder ?? tripFilters?.clientFolder ?? activeFolder ?? null;
  };

  return async (
    tripId,
    { fromDraft = false, folderComposition, preferredClientFolder } = {},
  ) => {
    const { trip, days, initialExtras } = await hydrateTripCore(tripId);

    // The template picker already knows exactly which of our folders this
    // trip is being designed from — use it directly rather than have
    // resolveClientFolder re-derive it from the global active folder / trip
    // org, which can race against org data still loading.
    const clientFolder =
      preferredClientFolder ?? (await resolveClientFolder(trip));

    const isTemplateFolder =
      isForeign(trip.organisationId) ||
      isForeign(trip.clientFolder?.organization?.id);
    if (
      !folderComposition &&
      isTemplateFolder &&
      clientFolder?.travelers?.length > 0
    ) {
      folderComposition = generateTravelerComposition(clientFolder);
    }

    const preserveFolderComposition = Boolean(folderComposition);

    // Build countries from the trip itself so none is ever dropped; enrich
    // label/position from the (default-locale) countries list when available.
    // Guarded so a failure to load the list never aborts the hydrate.
    let countries = (trip.countries ?? []).map(tripCountry => ({
      label: tripCountry.title,
      value: tripCountry.id,
      position: {
        lat: parseFloat(tripCountry.lat),
        lng: parseFloat(tripCountry.lon),
      },
    }));

    try {
      const countriesList = (await getCatalogCountries())?.data ?? [];

      countries = (trip.countries ?? []).map(tripCountry => {
        const match = countriesList.find(c => c.id === tripCountry.id);

        return {
          label: match?.name ?? tripCountry.title,
          value: tripCountry.id,
          position: {
            lat: parseFloat(match?.lat ?? tripCountry.lat),
            lng: parseFloat(match?.lon ?? tripCountry.lon),
          },
        };
      });
    } catch {
      // eslint-disable-next-line no-console
      console.error(
        'Failed to enrich countries from list; using trip countries as-is',
      );
    }

    setInitialExtras(initialExtras);

    pushEvent(GTM_EVENTS.TRIP_OPENED, {
      trip_id: tripId,
      trip_name: trip.name,
      trip_duration: trip.duration,
      days_count: days.length,
      source: fromDraft
        ? TRIP_ORIGINS.DRAFT
        : trip?.template
          ? TRIP_ORIGINS.TEMPLATE
          : TRIP_ORIGINS.TRIP,
    });

    const storedDeparture = trip?.startDate ? new Date(trip.startDate) : null;

    setTripFilters(prev => ({
      ...prev,
      programName: trip.name,
      tripDuration: trip.duration,
      dmcId: trip.dmc?.id,
      // Kept so the wizard recap can name the operating DMC on a seeded trip —
      // dmcId alone is just an id the review summary can't resolve to a name.
      dmcLabel: trip.dmc?.displayName,
      countries,
      startLocation: trip.startLocation,
      endLocation: trip.endLocation,
      // Blanked rather than clamped: hydration always lands in the wizard,
      // whose dates step then forces the user to pick a valid date.
      departureDate: isInvalidDepartureDate(storedDeparture)
        ? null
        : storedDeparture,
      templateId: trip?.template?.id,
      adultPax: preserveFolderComposition
        ? folderComposition.adults
        : trip?.adultPax,
      // Both branches normalize to the same ISO-string shape — the folder's
      // birthdates come straight off the API's traveler records, which don't
      // carry the same format guarantee as an already-hydrated trip.
      childrenBirthdates: preserveFolderComposition
        ? folderComposition.childrenBirthdates?.map(dateString =>
            new Date(dateString).toISOString(),
          )
        : trip?.childrenBirthdates?.map(dateString =>
            new Date(dateString).toISOString(),
          ),
      tripId: trip.id,
      locale: trip?.locale,
      // Our own folder for this trip, resolved above: the trip's when it is
      // ours, the one we are working in otherwise.
      clientFolder: clientFolder ?? prev?.clientFolder,
      composition: preserveFolderComposition
        ? {
            ...folderComposition,
            childrenBirthdates: folderComposition.childrenBirthdates?.map(
              dateString => new Date(dateString).toISOString(),
            ),
          }
        : (trip?.tripFilters?.composition ?? prev?.composition),
      themes: trip?.tripFilters?.themes ?? [],
      styles: trip?.tripFilters?.styles ?? [],
      brief: trip?.tripFilters?.brief ?? '',
    }));

    // Keep booking's customerFile in sync with the same folder just resolved
    // above for tripFilters — otherwise it can keep a stale override from a
    // previously hydrated trip.
    if (clientFolder) {
      setCustomerFile(clientFolder);
    }

    // That folder becomes the active one
    if (clientFolder && clientFolder.id !== activeFolder?.id) {
      openFolder(clientFolder);
    }
  };
}
